/* ==========================================================================
   ux-travel-lighter — the comparison table's progress bar on a phone, and
   mouse drag-to-scroll for the same scroller.

   Loaded only from sections/ux-travel-lighter.liquid, which renders the
   comparison only when it has rows.

   Below 750px the 556px table sits in a horizontal scroller and the frame
   draws a 4px bar under it. The bar is not a scrollbar: it is a picture of
   one. This measures the scroller and hands the CSS two numbers on the
   comparison wrapper — `--ux-tl-size`, the visible share of the table, which
   sizes the thumb, and `--ux-tl-progress`, how far along it the reader is,
   which moves it — and sets `data-scrollable` while there is anything to
   scroll. The CSS only shows the bar under that attribute and below 750px, so
   a table that fits, a wider screen, or this file never arriving all leave
   the bar hidden and the table exactly as usable.

   Scroll events are read once per frame; a ResizeObserver on the scroller and
   on the table covers rotation, the editor's viewport switch and the late
   fonts and images that change the table's width.

   Teardown: the listeners and the observer come off on
   `shopify:section:unload`, so the theme editor can re-render the section as
   often as it likes.
   ========================================================================== */

(function () {
  'use strict';

  const ROOT = '[data-section-type="ux-travel-lighter"]';
  const COMPARE = '.ux-travel-lighter__compare';
  const SCROLLER = '[data-ux-tl-scroller]';

  /* One entry per rendered section, so the editor can take a section's
     listeners down without touching another's. */
  const wired = new WeakMap();

  function measure(compare, scroller) {
    const range = scroller.scrollWidth - scroller.clientWidth;

    if (range <= 1) {
      compare.removeAttribute('data-scrollable');
      compare.style.removeProperty('--ux-tl-size');
      compare.style.removeProperty('--ux-tl-progress');
      return;
    }

    const size = scroller.clientWidth / scroller.scrollWidth;
    const progress = Math.min(1, Math.max(0, scroller.scrollLeft / range));

    compare.setAttribute('data-scrollable', '');
    compare.style.setProperty('--ux-tl-size', size.toFixed(4));
    compare.style.setProperty('--ux-tl-progress', progress.toFixed(4));
  }

  /* A mouse cannot swipe a native scroller, and the bar under it is a
     picture, not a scrollbar: a desktop browser at phone width, or the theme
     editor's mobile preview, had no way along the table but shift + wheel.
     Dragging with the mouse scrolls it. Touch and pen keep the browser's own
     scrolling — this never runs for them. */
  function dragToScroll(compare, scroller) {
    const THRESHOLD = 4;
    let drag = null;

    const onDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!compare.hasAttribute('data-scrollable')) return;

      drag = { id: event.pointerId, x: event.clientX, left: scroller.scrollLeft, moved: false };
    };

    const onMove = (event) => {
      if (!drag || event.pointerId !== drag.id) return;

      const dx = event.clientX - drag.x;

      if (!drag.moved) {
        if (Math.abs(dx) < THRESHOLD) return;

        drag.moved = true;
        scroller.setPointerCapture(drag.id);
        compare.setAttribute('data-dragging', '');
      }

      event.preventDefault();
      scroller.scrollLeft = drag.left - dx;
    };

    const onUp = (event) => {
      if (!drag || event.pointerId !== drag.id) return;

      if (drag.moved) {
        compare.removeAttribute('data-dragging');
        if (scroller.hasPointerCapture(drag.id)) scroller.releasePointerCapture(drag.id);

        /* The release is not a click on whatever sat under the pointer. */
        scroller.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => scroller.removeEventListener('click', swallow, true), 0);
      }

      drag = null;
    };

    const swallow = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    /* The cut-out images would otherwise start a native image drag. */
    const onDragStart = (event) => event.preventDefault();

    scroller.addEventListener('pointerdown', onDown);
    scroller.addEventListener('pointermove', onMove);
    scroller.addEventListener('pointerup', onUp);
    scroller.addEventListener('pointercancel', onUp);
    scroller.addEventListener('dragstart', onDragStart);

    return () => {
      compare.removeAttribute('data-dragging');
      scroller.removeEventListener('pointerdown', onDown);
      scroller.removeEventListener('pointermove', onMove);
      scroller.removeEventListener('pointerup', onUp);
      scroller.removeEventListener('pointercancel', onUp);
      scroller.removeEventListener('dragstart', onDragStart);
    };
  }

  function stop(root) {
    const entry = wired.get(root);

    if (!entry) return;

    entry.unwatch();

    wired.delete(root);
  }

  function start(root) {
    if (wired.has(root)) return;

    const compare = root.querySelector(COMPARE);
    const scroller = compare?.querySelector(SCROLLER);

    if (!scroller) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      measure(compare, scroller);
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    scroller.addEventListener('scroll', schedule, { passive: true });

    const observer = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;

    if (observer) {
      observer.observe(scroller);
      if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    } else {
      window.addEventListener('resize', schedule);
    }

    const undrag = dragToScroll(compare, scroller);

    wired.set(root, {
      unwatch: () => {
        undrag();
        if (frame) cancelAnimationFrame(frame);
        scroller.removeEventListener('scroll', schedule);
        if (observer) {
          observer.disconnect();
        } else {
          window.removeEventListener('resize', schedule);
        }
      },
    });

    measure(compare, scroller);
  }

  function roots(scope) {
    return Array.from(scope.querySelectorAll(ROOT));
  }

  document.addEventListener('shopify:section:load', (event) => {
    roots(event.target).forEach(start);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    roots(event.target).forEach(stop);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => roots(document).forEach(start), { once: true });
  } else {
    roots(document).forEach(start);
  }
})();
